import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { Box3 } from "three";
import { v3 } from "oc-threejs/short3";
import { getMMR } from "../RichMetadata";
import { MEckadapter } from "../../metamodell";
import { Ecke } from "./Ecke";
import { Part } from "./Part";
import { ZElement } from "./ZElement";
import { Pfosten } from "./Pfosten";
import { Aufbau } from "./Aufbau";
import { BestelllistenEintrag, StücklistenIterator } from "./Stückliste";
import { Segment } from "./Segment";
import { ghostPool } from "./Arrangement";
import { ModelGhost } from "oc-jaco3d/arrangement/ModelGhost";

export class Eckadapter extends Jaco {
  static CLASS_NAME = "Eckadapter";

  isEckadapter = true;
  declare parent: Segment;

  // ----------------------------------------------------------------------------------
  // persistierte Werte - Zustand, werden mit der Konfiguration gespeichert
  // ----------------------------------------------------------------------------------

  // nix

  // ----------------------------------------------------------------------------------
  // abgeleitete Werte - d.h. sind letztlich nur berechnet (in update3d) und gecached, kein Zustand
  // Achtung, diese Werte muss man immer ganz besonders im Auge haben, kann sein,
  // dass sie fälschlich doch als Zustand dienen, und das führt dann zu schwierigen Bugs
  // ----------------------------------------------------------------------------------

  meta: MEckadapter = null;
  nachbarLinks: Part | Pfosten | Aufbau = null;
  nachbarRechts: Part | Pfosten | Aufbau = null;
  model: ModelGhost = null;
  box = new Box3();
  bestellliste: BestelllistenEintrag[] = [];
  gesamtpreis = 0;
  size = v3();
  ecke!: Ecke;
  gapLinks = 0;
  gapRechts = 0;
  elemente: ZElement[] = [];

  constructor(parent: Jaco) {
    super(parent);
  }

  get eckadapterDelta() {
    const segment = this.segment;
    const eckenIndex = segment.ecken.findIndex((e) => e === this.ecke);
    return segment.eckadapterDelta[eckenIndex];
  }

  traverse(iterator: (j: Jaco) => void) {
    super.traverse(iterator);
    if (this.model) this.model.traverse(iterator);
  }

  update3d() {
    super.update3d();
    this.model = ghostPool(this).clearInstance(this.model);
    const { model, box } = this.getModel();
    this.model = model;
    this.box.copy(box);
    box.getSize(this.size);
    model.update3d();
  }

  getModel() {
    const mmr = getMMR(this.meta);
    const box = APP.modelsHelper.getBoundingBox(mmr)?.clone()!;
    const nachbar = (
      (this.nachbarLinks as Pfosten)?.isPfostenAnEcke ? this.nachbarRechts : this.nachbarLinks
    ) as ZElement;
    const modelLänge = box.getSize(v3()).y;
    const nachbarBox = nachbar?.getBox();
    const länge = nachbarBox ? nachbarBox.getSize(v3()).y : modelLänge;
    const positionY = Math.max(nachbar?.positionY ?? 0, nachbarBox?.min.y ?? 0);
    const model = ghostPool(this).getForModelMaterialRelation(this, mmr, {
      size: v3(Infinity, länge, Infinity),
      fixGridPosition: v3(0.5, 0, 0.5),
      gehrung45: false,
    });
    box.min.y = positionY;
    box.max.y = box.min.y + länge;
    // box.max.x += 0.0025;
    model.position.setY(positionY);
    model.update3d();
    return { model, box };
  }

  reset() {
    super.resetCoordinates();
    this.elemente = [];
    if (this.ecke != null) {
      this.ecke.pfosten = null;
    }
    this.ecke = null;
    this.model = null;
    this.nachbarLinks = null;
    this.nachbarRechts = null;
    this.gapLinks = 0;
    this.gapRechts = 0;
  }

  get segment(): Segment {
    const p = this.parent;
    return p?.isSegment ? p : null;
  }

  getBox() {
    return this.box.clone();
  }

  get breite() {
    return this.size.x;
  }

  get snap() {
    return (this.nachbarLinks as Pfosten)?.isPfostenAnEcke ? -1 : 1;
  }

  set snap(v: number) {
    // nothin..
  }

  getNachbarn() {
    return [this.nachbarLinks, this.nachbarRechts].filter((n) => n != null) as Array<
      Part | Pfosten | Aufbau
    >;
  }

  clone() {
    const clone = new Eckadapter(this.parent);
    clone.copy(this);
    return clone;
  }

  copy(other: Eckadapter) {
    // das hier müssen exakt die persistierten oder session Werte sein!
    // zusätzlich solche, die gecloned werden müssen
    this.meta = other.meta;
    this.size.copy(other.size);
    return this;
  }

  toJSON() {
    const json = super.toJSON();
    json.type = (this.constructor as any).CLASS_NAME;
    json.meta = this.meta.Key;
    return json;
  }

  fromJSON(json: any = {}) {
    super.fromJSON(json);
    this.meta = APP.richMetadata.findAnyObjekt(json.meta);
    return this;
  }

  getMeta() {
    return this.meta;
  }

  getBoxUnclipped() {
    return APP.modelsHelper.getBoundingBox(getMMR(this.getMeta()))?.clone();
  }

  collectArtikel(iterator: StücklistenIterator) {
    iterator({ ObjektMeta: this.meta, JacoId: this.id });
  }
}
